import { useContext, useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";
import {
  Content,
  Text,
  Heading,
  ActionButton,
  AlertDialog,
  Checkbox,
  ContextualHelp,
  DialogContainer,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  LinkButton,
  Button,
  DialogTrigger,
  CustomDialog,
  CloseButton,
  mergeStyles,
} from "@react-spectrum/s2";
import ExportIcon from "@react-spectrum/s2/icons/Export";
import UploadToCloudIcon from "@react-spectrum/s2/icons/UploadToCloud";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import ImagesIcon from "@react-spectrum/s2/icons/Images";
import NoElementsIcon from "@react-spectrum/s2/illustrations/linear/NoElements";
import BrandIcon from "@react-spectrum/s2/icons/Brand";
import { store } from "@app/store";

import { hasImageVariationsStep } from "@app/hooks/videoWizard";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { supportsAudioMuxDownloadTemplate } from "@app/hooks/useWizardStepActions";
import {
  getStructureAssetNames,
  getStructureVariationKeys,
  getStructureVariationProgress,
  hasPendingStructureVariations,
} from "../wizardSteps";
import { TemplateAssets } from "../TemplateAssets";
import { CampaignVisualsPreview } from "../CampaignVisualsPreview";
import { EditVariationDialogContainer } from "../EditVariationDialogContainer";
import { ScrapedImages } from "../ScrapedImages";
import { UploadedImages } from "../UploadedImages";
import { Step, stepFooterStyle } from "./Step";
import { DevToolbarItem } from "../DevToolbar";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };
import { headingStyle } from "../style" with { type: "macro" };

const FIREFLY_TERMS_TITLE = "Confirm use of images with Adobe Firefly";

const FIREFLY_TERMS_BODY =
  "A new set of image variations will be generated using Adobe Firefly. To use this service, you must have the rights or permission to use any third-party images as reference. You are responsible for ensuring compliance with all copyright and licensing requirements.";

// Generating every variation, as opposed to a list of keys.
const GENERATE_ALL = "all";

const ImageLibrary = ({ styles, videoId, queries, onDrop, onMenuAction }) => (
  <div
    className={mergeStyles(
      style({
        display: "flex",
        flexDirection: "column",
        gap: 12,
        // padding: 24,
        // paddingTop: 8,
      }),
      styles,
    )}
  >
    <div
      id="assets-menu"
      className={style({
        marginStart: "auto",
        position: "absolute",
        top: 12,
        insetStart: { default: 16, md: "unset" },
        insetEnd: { md: 24 },
        zIndex: 10,
      })}
    />
    <Tabs
      aria-label="Image library"
      density="compact"
      styles={style({
        size: "full",
      })}
    >
      <TabList>
        <Tab id="extracted">
          <ExportIcon />
          <Text>Extracted images</Text>
        </Tab>
        <Tab id="uploaded">
          <UploadToCloudIcon />
          <Text>Your assets</Text>
        </Tab>
      </TabList>
      <TabPanel id="extracted">
        <div
          className={style({
            size: "full",
            overflowY: "auto",
            marginX: -24,
            paddingX: 24,
          })}
        >
          <ScrapedImages
            videoId={videoId}
            queries={queries}
            onMenuAction={onMenuAction}
          />
        </div>
      </TabPanel>
      <TabPanel id="uploaded">
        <div
          className={style({
            size: "full",
            overflowY: "auto",
            marginX: -24,
            paddingX: 24,
          })}
        >
          <UploadedImages
            videoId={videoId}
            onDrop={onDrop}
            onMenuAction={onMenuAction}
            styles={style({ marginX: -24, paddingX: 24 })}
          />
        </div>
      </TabPanel>
    </Tabs>
  </div>
);

/**
 * The customer's branding assets and, when the step configures a
 * `campaignStructure`, the image variations and template assets it shows —
 * rendered as in `CampaignVisualsStep` (its assets no longer listed by
 * `TemplateAssets`), but a variation that was never generated waits for the
 * user to generate it (or to drop an image onto it).
 */
export const BrandingStep = ({
  onContinue,
  onBack,
  continueLabel,
  campaignStructure,
  name,
  description,
}) => {
  const {
    isStepPending,
    videoId,
    template,
    video,
    createVideo,
    updateVideo,
    customerBranding,
    selectedCampaign,
    selectedPersona,
    selectedAssets = [],
    allScrapedQueries,
    areAllVisualsSelected,
    handleUploadAssets,
    handleAssetMenuAction,
    handleDownloadAudioMux,
    audioMuxDownloadUrl,
    audioMuxDownloadPending,
    agreeToTerms,
    setAgreeToTerms,
    setEditVariation,
    stepActionCtx,
    scrapedColors,
    handleColorChange,
  } = useContext(VideoWizardContext);

  const hasVariations = useMemo(
    () => getStructureVariationKeys(campaignStructure).length > 0,
    [campaignStructure],
  );

  // Rendered by the campaign structure rather than `TemplateAssets`.
  const structureAssetNames = useMemo(
    () => getStructureAssetNames(campaignStructure),
    [campaignStructure],
  );

  const hasStructure = hasVariations || structureAssetNames.length > 0;

  const imageVariations = hasVariations ? stepActionCtx?.imageVariations : null;

  // Arms the pipeline so a variation that was already generated keeps
  // regenerating from the edit dialog; one that never was waits for
  // `generate()`.
  const { run } = imageVariations ?? {};

  useEffect(() => {
    run?.();
  }, [run]);

  const isVisualsPending =
    hasVariations &&
    hasPendingStructureVariations(
      campaignStructure,
      imageVariations?.variations,
      video.data?.assets,
    );

  const isMobile = useStore(store, (state) => state.isMobile);

  const requiresFireflyTerms =
    hasImageVariationsStep(template.data) || hasVariations;

  // Generation needs a campaign to write the prompts for.
  const isGenerateDisabled = !selectedCampaign?.id;

  // A generation waiting for the Firefly terms to be agreed to: the keys to
  // generate, or `GENERATE_ALL`.
  const [pendingGenerate, setPendingGenerate] = useState(null);
  const [pendingContinue, setPendingContinue] = useState(false);

  const dismissFireflyTerms = () => {
    setPendingGenerate(null);
    setPendingContinue(false);
  };

  const handleContinue = () => {
    if (isMobile && requiresFireflyTerms && !agreeToTerms) {
      setPendingContinue(true);
    } else {
      onContinue();
    }
  };

  const requestGenerate = (keys = GENERATE_ALL) => {
    if (agreeToTerms === true) {
      imageVariations?.generate(keys === GENERATE_ALL ? undefined : keys);
    } else {
      setPendingGenerate(keys);
    }
  };

  const templateAssets = template.data?.assets || [];

  // The image variations the campaign structure shows count as selected once
  // they have an image, like the assets.
  const variationProgress = hasVariations
    ? getStructureVariationProgress(
        campaignStructure,
        imageVariations?.variations,
        video.data?.assets,
        templateAssets.map((asset) => asset.name),
      )
    : { total: 0, selected: 0 };

  const countedAssets = (assets) =>
    assets?.filter((asset) => asset.type !== "audio")?.length ?? 0;

  const brandFetchUrl = (() => {
    try {
      if (video.data?.customer?.["Company-website-URL"]) {
        const companyWebsiteUrl = new URL(
          video.data?.customer?.["Company-website-URL"],
        );
        return new URL(
          companyWebsiteUrl.hostname.replace(/^www\./, ""),
          "https://brandfetch.com",
        );
      }
      return null;
    } catch (error) {
      return null;
    }
  })();

  if (isStepPending) {
    return (
      <Step.Loading
        title="Setting up your project"
        subtitle="This may take a few moments, so please keep this tab open."
        ariaLabel="Setting up your project"
        icon={<NoElementsIcon />}
      />
    );
  }

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 24,
        })}
      >
        <div>
          <Heading
            styles={style(headingStyle({ size: "lg", marginBottom: 0 }))}
          >
            {name || "Branding & visuals"}
          </Heading>
          <Content>
            <p
              className={style({
                font: "body",
                maxWidth: 600,
                textWrap: "pretty",
              })}
            >
              {description ||
                "Select images that represent the customer's brand and visual identity. These images will be used to generate tailored content for the video. You can upload images directly or drag and drop images scraped from the customer's website and brand center."}
            </p>
          </Content>
          {supportsAudioMuxDownloadTemplate(template.data?.id) &&
            !!audioMuxDownloadUrl && (
              <ActionButton
                isPending={audioMuxDownloadPending}
                onPress={() => {
                  void handleDownloadAudioMux();
                }}
                isQuiet
                size="S"
                styles={style({ marginTop: 12 })}
              >
                <DownloadIcon />
                <Text>Download muxed audio</Text>
              </ActionButton>
            )}
        </div>

        <div
          className={style({ display: "flex", alignItems: "baseline", gap: 8 })}
        >
          <Text styles={style({ font: "detail" })}>
            {`${countedAssets(selectedAssets) + variationProgress.selected}/${countedAssets(templateAssets) + variationProgress.total} selected`}
          </Text>
          {!!brandFetchUrl && (
            <LinkButton
              href={brandFetchUrl}
              target="_blank"
              variant="secondary"
              staticColor="auto"
              size="S"
              styles={style({ marginStart: 4 })}
            >
              <BrandIcon />
              <Text>Check brand info</Text>
            </LinkButton>
          )}
        </div>

        <TemplateAssets excludedAssetNames={structureAssetNames} />

        {hasStructure && (
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 16,
              // maxWidth: 768,
            })}
          >
            <CampaignVisualsPreview
              campaignStructure={campaignStructure}
              selectedCampaign={selectedCampaign}
              selectedPersona={selectedPersona}
              // Without the campaign prompt footer, there is nothing to edit
              // the fields with.
              canEditCampaign={false}
              video={video}
              templateData={template.data}
              imageVariations={imageVariations}
              isGenerateDisabled={isGenerateDisabled}
              onEditVariation={setEditVariation}
              onGenerateVariation={(variation) =>
                requestGenerate([variation.key])
              }
              onUploadVariation={handleUploadAssets}
              colors={scrapedColors}
              onUploadAsset={handleUploadAssets}
              onDeleteAsset={(asset) => handleAssetMenuAction("delete", asset)}
              onColorChange={handleColorChange}
              styles={style({ maxWidth: "full" })}
            />
          </div>
        )}
      </Step>

      {hasVariations && <EditVariationDialogContainer />}

      <DialogContainer onDismiss={dismissFireflyTerms}>
        {(pendingGenerate !== null || pendingContinue) && (
          <AlertDialog
            title={FIREFLY_TERMS_TITLE}
            primaryActionLabel="Agree"
            cancelLabel="Cancel"
            onPrimaryAction={() => {
              setAgreeToTerms(true);
              dismissFireflyTerms();
              if (pendingContinue) {
                onContinue();
              } else {
                imageVariations?.generate(
                  pendingGenerate === GENERATE_ALL
                    ? undefined
                    : pendingGenerate,
                );
              }
            }}
            onCancel={dismissFireflyTerms}
          >
            {FIREFLY_TERMS_BODY}
          </AlertDialog>
        )}
      </DialogContainer>

      {!isMobile && (
        <ImageLibrary
          videoId={videoId}
          queries={allScrapedQueries}
          onDrop={handleUploadAssets}
          onMenuAction={handleAssetMenuAction}
          styles={style({
            gridArea: "sidebar",
            boxSizing: "border-box",
            alignSelf: "end",
            minWidth: 320,
            maxWidth: 460,
            height: `calc(100vh - ${size(250)})`,
            marginStart: 48,
            backgroundColor: "elevated",
            borderRadius: "lg",
            padding: 24,
            paddingTop: 8,
            boxShadow: "emphasized",
            position: "sticky",
            bottom: size(102),
          })}
        />
      )}

      {isMobile && (
        <Step.Footer>
          <div
            className={mergeStyles(
              stepFooterStyle,
              style({
                width: "auto",
                padding: 12,
                backgroundColor: "elevated",
                borderRadius: "pill",
                boxShadow: "elevated",
              }),
            )}
          >
            <DialogTrigger>
              <Button variant="premium" aria-label="Open image library">
                <ImagesIcon />
              </Button>
              <CustomDialog size="fullscreenTakeover">
                {({ close }) => (
                  <>
                    <ImageLibrary
                      videoId={videoId}
                      queries={allScrapedQueries}
                      onDrop={handleUploadAssets}
                      onMenuAction={handleAssetMenuAction}
                      styles={style({
                        height: "full",
                        marginTop: 24,
                      })}
                    />
                    <CloseButton
                      styles={style({
                        position: "absolute",
                        top: 12,
                        insetEnd: 12,
                      })}
                    />
                  </>
                )}
              </CustomDialog>
            </DialogTrigger>
          </div>
        </Step.Footer>
      )}

      <Step.Actions
        onBack={onBack}
        onContinue={handleContinue}
        continueLabel={continueLabel}
        continueVariant="primary"
        isContinueDisabled={
          !areAllVisualsSelected ||
          (!isMobile && requiresFireflyTerms && !agreeToTerms) ||
          isVisualsPending
        }
      >
        {!isMobile && requiresFireflyTerms && (
          <div
            className={style({
              display: "flex",
              gap: 8,
              alignItems: "center",
            })}
          >
            <Checkbox
              isSelected={agreeToTerms}
              isRequired
              onChange={(isSelected) => setAgreeToTerms(isSelected)}
            >
              {FIREFLY_TERMS_TITLE}
            </Checkbox>
            <ContextualHelp variant="info" size="S">
              <Heading>{FIREFLY_TERMS_TITLE}</Heading>
              <Content>{FIREFLY_TERMS_BODY}</Content>
            </ContextualHelp>
          </div>
        )}
      </Step.Actions>

      {hasStructure && (
        <DevToolbarItem
          isPending={template.isPending}
          onPress={() => template.refetch()}
        >
          Refetch template
        </DevToolbarItem>
      )}

      <DevToolbarItem
        isPending={updateVideo.isPending}
        onPress={() => {
          const keys = Array.from(
            (template.data?.imageVariations ?? []).reduce(
              (acc, variation) => acc.add(variation.key),
              new Set(),
            ),
          );

          const assetsToRemove = video.data?.assets?.filter((asset) =>
            keys.includes(asset.name),
          );

          assetsToRemove?.forEach((asset) => {
            if (asset.referenceKey) {
              assetsToRemove.push({ name: asset.referenceKey });
            }
          });

          const assets = video.data?.assets
            ?.filter((asset) => !keys.includes(asset.name))
            ?.map(({ labeling, ...asset }) => asset);

          updateVideo.mutate({
            audiencesVariations: null,
            abstractVariations: null,
            imageVariations: [],
            assets,
          });
        }}
      >
        Regenerate visuals
      </DevToolbarItem>
    </>
  );
};
